import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { USE_AUDIO_ASSET_IN_PROJECT_SUCCESS } from 'js/actionCreators/audioActions';
import { getCountriesByLanguage, VoiceConfig } from 'js/editor/AudioDrawers/voices';
import { VoiceoverGenerationOutputs, VoiceoverGenerationStatus } from 'js/types';

import { ProjectScript } from './projectScripts';

export enum VoiceoverGenerationStage {
  Voice = 'Voice',
  Result = 'Result'
}

export type VoiceoversState = {
  generationStage: VoiceoverGenerationStage | null;
  scriptName: string;
  scriptText: string;
  selectedLanguage: string;
  selectedRegion: string;
  selectedVoice: VoiceConfig | null;
  voiceoverGenerationStatus: VoiceoverGenerationStatus;
  voiceoverGenerationResults: VoiceoverGenerationOutputs;
  generationStartedFromScript: ProjectScript | null;
};

const initialState: VoiceoversState = {
  generationStage: null,
  scriptName: '',
  scriptText: '',
  selectedLanguage: '',
  selectedRegion: '',
  selectedVoice: null,
  voiceoverGenerationStatus: VoiceoverGenerationStatus.NOT_STARTED,
  voiceoverGenerationResults: {
    url: '',
    id: null,
    error: null,
    assetId: null,
    rating: null
  },
  generationStartedFromScript: null
};

const voiceovers = createSlice({
  name: 'voiceovers',
  initialState,
  reducers: {
    setVoiceoverStage(state, action: PayloadAction<{ stage: VoiceoverGenerationStage | null }>) {
      state.generationStage = action.payload.stage;
    },
    setVoiceoverScriptName(state, action: PayloadAction<{ scriptName: string }>) {
      state.scriptName = action.payload.scriptName;
    },
    setVoiceoverScriptText(state, action: PayloadAction<{ scriptText: string }>) {
      state.scriptText = action.payload.scriptText;
    },
    setSelectedLanguage(state, action: PayloadAction<{ selectedLanguage: string }>) {
      state.selectedLanguage = action.payload.selectedLanguage;
      state.selectedVoice = null;

      const availableRegions = getCountriesByLanguage(action.payload.selectedLanguage);
      if (availableRegions.length === 1) {
        state.selectedRegion = availableRegions[0].countryCode; // Automatically select the only available region
      } else {
        state.selectedRegion = ''; // Reset selected region when language changes
      }
    },
    setSelectedRegion(state, action: PayloadAction<{ selectedRegion: string }>) {
      state.selectedRegion = action.payload.selectedRegion;
      state.selectedVoice = null;
    },
    setSelectedVoice(state, action: PayloadAction<{ selectedVoice: VoiceConfig }>) {
      state.selectedVoice = action.payload.selectedVoice;
    },
    previewVoiceoverVoice(_state, _action: PayloadAction<{ language: string; voice: string; gender: string }>) {},
    generateVoiceover(state) {
      state.generationStage = VoiceoverGenerationStage.Result;
      state.voiceoverGenerationStatus = VoiceoverGenerationStatus.PENDING;
      state.voiceoverGenerationResults = initialState.voiceoverGenerationResults;
    },
    voiceoverGenerationSuccess(
      state,
      action: PayloadAction<{ voiceoverUrl: string; voiceoverId: string; voiceoverAssetId: number }>
    ) {
      state.generationStage = VoiceoverGenerationStage.Result;
      state.voiceoverGenerationStatus = VoiceoverGenerationStatus.SUCCEEDED;
      state.voiceoverGenerationResults = {
        url: action.payload.voiceoverUrl,
        id: action.payload.voiceoverId,
        assetId: action.payload.voiceoverAssetId,
        error: null,
        rating: null
      };
    },
    voiceoverGenerationFailed(state, action: PayloadAction<{ error: string }>) {
      state.generationStage = VoiceoverGenerationStage.Result;
      state.voiceoverGenerationStatus = VoiceoverGenerationStatus.FAILED;
      state.voiceoverGenerationResults = {
        url: null,
        id: null,
        assetId: null,
        error: action.payload.error,
        rating: null
      };
    },
    startFromScript(state, action: PayloadAction<{ script: ProjectScript }>) {
      state.generationStartedFromScript = action.payload.script;
      state.scriptName = action.payload.script.title?.trim() || '';
      state.scriptText = action.payload.script.projectScript.trim() || '';
      state.generationStage = VoiceoverGenerationStage.Voice;
    },
    goBackToScript(state, _action: PayloadAction<{ script: ProjectScript }>) {
      state.generationStartedFromScript = null;
      state.scriptName = '';
      state.scriptText = '';
      state.generationStage = null;
    }
  },
  extraReducers: builder => {
    builder.addCase(USE_AUDIO_ASSET_IN_PROJECT_SUCCESS, state => {
      state.generationStage = null;
      state.scriptName = '';
      state.scriptText = '';
      state.selectedLanguage = '';
      state.selectedRegion = '';
      state.selectedVoice = null;
    });
  }
});

export const {
  setVoiceoverStage,
  setVoiceoverScriptName,
  setVoiceoverScriptText,
  setSelectedLanguage,
  setSelectedRegion,
  setSelectedVoice,
  previewVoiceoverVoice,
  generateVoiceover,
  voiceoverGenerationSuccess,
  voiceoverGenerationFailed,
  startFromScript,
  goBackToScript
} = voiceovers.actions;
export const { name: reducerName } = voiceovers;
export default voiceovers.reducer;
